import { NO_VIDEO_POSTER, isSafeAssetVideoSrc, isVideoAssetMimeType } from '../components/video-source';
import type { VideoPoster } from '../components/video-source';
import { isValidImageUrl } from './image-url';

/**
 * Shared "one asset, image or video" value. Authors never choose a media type: it is detected
 * from the asset library's type and MIME type, or from the file extension of a pasted URL, and
 * stored next to the source so renderers and inspectors never re-guess it.
 *
 * Pure and storefront-safe: no editor, Apollo or asset-browser imports (see bundle-boundaries test).
 */
export type MediaKind = 'image' | 'video';

export type MediaAssetValue = {
  kind: MediaKind | '';
  src: string;
  assetId: string;
  mimeType: string;
  width: number | null;
  height: number | null;
};

export const EMPTY_MEDIA_ASSET: MediaAssetValue = {
  kind: '',
  src: '',
  assetId: '',
  mimeType: '',
  width: null,
  height: null,
};

/** The flat props a block adopts to accept an image or a video. */
export type MediaAssetProps = {
  mediaAsset: MediaAssetValue;
  mediaPoster: VideoPoster;
  mediaDecorative: boolean;
};

export const DEFAULT_MEDIA_ASSET_PROPS: MediaAssetProps = {
  mediaAsset: { ...EMPTY_MEDIA_ASSET },
  mediaPoster: { ...NO_VIDEO_POSTER },
  mediaDecorative: false,
};

export const MEDIA_IMAGE_EXTENSIONS: readonly string[] = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg'];
export const MEDIA_VIDEO_EXTENSIONS: readonly string[] = ['mp4', 'webm'];

const EXTENSION_MIME: Record<string, string> = {
  mp4: 'video/mp4',
  webm: 'video/webm',
};

/**
 * The `type` for a video `<source>`: the stored MIME type when there is one; otherwise taken from the
 * URL extension, so a `.webm` with no stored type is not labelled MP4. Unknown extensions fall back
 * to MP4, which only affects which source the browser may skip, since it sniffs the real format.
 */
export function videoSourceMimeType(src: string, mimeType: string): string {
  return mimeType || EXTENSION_MIME[mediaUrlExtension(src)] || 'video/mp4';
}

export const MEDIA_URL_ERROR =
  'Use a link to a .jpg, .jpeg, .png, .gif, .webp, .avif, .svg, .mp4 or .webm file so the type can be detected.';

function kindFromLibraryType(type: unknown): MediaKind | null | undefined {
  if (type === 'image' || type === 'svg') return 'image';
  if (type === 'video') return 'video';
  return undefined; // absent or unknown: defer to the MIME type
}

function kindFromMime(mimeType: unknown): MediaKind | null | undefined {
  if (typeof mimeType !== 'string' || mimeType.trim() === '') return undefined;
  const mime = mimeType.trim().toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return isVideoAssetMimeType(mime) ? 'video' : null; // only MP4/WebM play
  return null;
}

/** Lower-cased extension of the URL path, ignoring query string and fragment. */
export function mediaUrlExtension(src: unknown): string {
  if (typeof src !== 'string') return '';
  const path = src.trim().split(/[?#]/)[0];
  const match = /\.([a-z0-9]+)$/i.exec(path);
  return match ? match[1].toLowerCase() : '';
}

function kindFromExtension(src: unknown): MediaKind | null {
  const extension = mediaUrlExtension(src);
  if (MEDIA_VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (MEDIA_IMAGE_EXTENSIONS.includes(extension)) return 'image';
  return null;
}

/**
 * Detects image vs video. The library's `type` and `mimeType` win; when both are present and
 * disagree, or the file is a video the block cannot play (only MP4/WebM), the answer is `null`
 * rather than a guess. Only when the library says nothing does the URL extension decide.
 */
export function detectMediaKind(asset: { type?: unknown; mimeType?: unknown; src?: unknown }): MediaKind | null {
  const fromType = kindFromLibraryType(asset.type);
  const fromMime = kindFromMime(asset.mimeType);
  if (fromType === undefined && fromMime === undefined) return kindFromExtension(asset.src);
  if (fromType !== undefined && fromMime !== undefined && fromType !== fromMime) return null;
  return (fromType === undefined ? fromMime : fromType) ?? null;
}

function positiveDimension(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** One rule per kind: videos need a safe asset URL, images the same check the image pickers use. */
function isSafeMediaSrc(kind: MediaKind, src: string): boolean {
  return kind === 'video' ? isSafeAssetVideoSrc(src) : isValidImageUrl(src);
}

/** Builds the stored value for a pasted URL, or `null` when its type cannot be detected. */
export function mediaAssetFromUrl(url: string): MediaAssetValue | null {
  const src = url.trim();
  if (src === '') return { ...EMPTY_MEDIA_ASSET };
  const kind = kindFromExtension(src);
  if (kind === null || /[\r\n\t\\]/.test(src)) return null;
  if (!isSafeMediaSrc(kind, src)) return null;
  return {
    ...EMPTY_MEDIA_ASSET,
    kind,
    src,
    mimeType: kind === 'video' ? EXTENSION_MIME[mediaUrlExtension(src)] : '',
  };
}

/** Builds the stored value for an asset chosen in the library, or `null` when it is unsupported. */
export function mediaAssetFromLibrary(asset: {
  id?: unknown;
  type?: unknown;
  mimeType?: unknown;
  url?: unknown;
  width?: unknown;
  height?: unknown;
}): MediaAssetValue | null {
  if (typeof asset.id !== 'string' || asset.id === '' || typeof asset.url !== 'string' || asset.url === '') return null;
  const kind = detectMediaKind({ type: asset.type, mimeType: asset.mimeType, src: asset.url });
  if (kind === null) return null;
  if (!isSafeMediaSrc(kind, asset.url)) return null;
  if (kind === 'video' && !isVideoAssetMimeType(asset.mimeType)) return null;
  return {
    kind,
    src: asset.url,
    assetId: asset.id,
    mimeType: typeof asset.mimeType === 'string' ? asset.mimeType.toLowerCase() : '',
    width: positiveDimension(asset.width),
    height: positiveDimension(asset.height),
  };
}

function isMimeTypeForKind(kind: MediaKind, mimeType: string): boolean {
  return kind === 'video' ? isVideoAssetMimeType(mimeType) : mimeType.toLowerCase().startsWith('image/');
}

/** Stored JSON is untrusted: anything malformed collapses to the empty value. */
export function normalizeMediaAsset(value: unknown): MediaAssetValue {
  if (!isRecord(value)) return { ...EMPTY_MEDIA_ASSET };
  const { kind, src } = value;
  if ((kind !== 'image' && kind !== 'video') || typeof src !== 'string' || src.trim() === '') {
    return { ...EMPTY_MEDIA_ASSET };
  }
  if (!isSafeMediaSrc(kind, src)) return { ...EMPTY_MEDIA_ASSET };
  const mimeType = typeof value.mimeType === 'string' ? value.mimeType : '';
  // A supplied MIME type must agree with the kind (and be playable for video); a blank one is allowed.
  if (mimeType !== '' && !isMimeTypeForKind(kind, mimeType)) return { ...EMPTY_MEDIA_ASSET };
  return {
    kind,
    src,
    assetId: typeof value.assetId === 'string' ? value.assetId : '',
    mimeType,
    width: positiveDimension(value.width),
    height: positiveDimension(value.height),
  };
}

export function isVideoMediaAsset(value: unknown): boolean {
  return normalizeMediaAsset(value).kind === 'video';
}

const VIDEO_ONLY_FIELDS = ['mediaPoster', 'mediaDecorative'] as const;

/** Hides the video-only controls unless the chosen asset is a video. Saved values are untouched here. */
export function resolveMediaAssetFields<Fields extends Record<string, unknown>>(
  mediaAsset: unknown,
  fields: Fields
): Fields {
  if (isVideoMediaAsset(mediaAsset)) return fields;
  return Object.fromEntries(
    Object.entries(fields).filter(([name]) => !(VIDEO_ONLY_FIELDS as readonly string[]).includes(name))
  ) as Fields;
}

/**
 * Leaves no stale video settings behind: when the asset is not a video the poster and decorative
 * flag return to their defaults (and the same object is returned when nothing needs to change, so
 * the edit stays a single history step). Call from the host block's `resolveData`.
 */
export function resolveMediaAssetData<Props extends Partial<MediaAssetProps>>(props: Props): Props {
  if (isVideoMediaAsset(props.mediaAsset)) return props;
  // Stored JSON is untrusted: a null or non-object poster is stale data to reset, not a crash.
  const poster: unknown = props.mediaPoster;
  const hasPoster = poster !== undefined && !(isRecord(poster) && poster.kind === 'none');
  if (!hasPoster && !props.mediaDecorative) return props;
  return {
    ...props,
    mediaPoster: { ...DEFAULT_MEDIA_ASSET_PROPS.mediaPoster },
    mediaDecorative: DEFAULT_MEDIA_ASSET_PROPS.mediaDecorative,
  };
}
